import React, {useEffect, useState} from 'react';
import {Button, Checkbox, FormControlLabel, FormGroup, MenuItem, Stack, TextField, Typography} from '@mui/material';
import useTextField from '../utility/hooks/useTextField';
import PropTypes from 'prop-types';

const AdmicityForm = (
    {
        title,
        textFields,
        options,
        handleSubmit,
        handleChange,
        errorCaption,
        buttonText,
        actionsContainerProps,
        buttonFullWidth = true,
        isButtonDisabled,
        setIsChildFormValid = null
    }, ref) => {
    const fields = textFields.map(field => useTextField({
        initialValue: field.initialValue ?? '',
        validate: field.validate,
        required: field.required ?? true,
        select: field.select ?? false
    }));
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const isFormValid = !fields.some(field => field.error || (field.value === '' && field.required));

    useEffect(() => {
        return () => {
            fields.forEach(field => field.reset());
            setSelectedOptions([]);
        };
    }, []);

    useEffect(() => {
        let activeOptionsIndexes = options?.reduce((result, option, index) => {
            if (option.initialValue) {
                result.push(index);
            }
            return result;
        }, []);
        setSelectedOptions(activeOptionsIndexes);
    }, [options]);

    useEffect(() => {
        if (setIsChildFormValid !== null) {
            setIsChildFormValid(isFormValid);
        }
    }, [isFormValid]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);
        const textFieldValues = textFields.reduce((obj, field, index) => {
            if (field.name) {
                obj[field.name] = fields[index].value;
            }

            return obj;
        }, {});

        const optionValues = options?.reduce((obj, option, index) => {
            obj[option.name] = selectedOptions.includes(index);
            return obj;
        }, {});

        try {
            await handleSubmit({...textFieldValues, ...optionValues});
        } catch (e) {
        } finally {
            setIsSubmitted(false);
        }
    };

    const handleSelectOption = (event, index) => {
        if (!event.target.checked) {
            setSelectedOptions(selectedOptions.filter(x => x !== index));
        } else {
            setSelectedOptions([...selectedOptions, index]);
        }
    };

    return (
        <form ref={ref} onChange={handleChange} onSubmit={onSubmit}>
            <Typography variant="h6" textAlign={'center'}>{title}</Typography>
            {
                textFields.map((field, index) => (
                    field.select
                        ? <TextField
                            key={`select-${index}`}
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            disabled={isSubmitted}
                            {...field}
                            {...fields[index]}
                        >
                            {field.options.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.title}
                                </MenuItem>
                            ))}
                        </TextField>
                        : <TextField
                            key={`textField-${index}`}
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            disabled={isSubmitted}
                            InputLabelProps={{
                                style: { overflow: 'visible' },
                            }}
                            {...field}
                            {...fields[index]}
                        />
                ))
            }
            {
                options
                    ? <FormGroup>
                        {
                            options.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    disabled={isSubmitted}
                                    control={<Checkbox
                                        checked={selectedOptions.includes(index)}
                                        onChange={(e) => handleSelectOption(e, index)}
                                    />}
                                    label={option.label}
                                />
                            ))
                        }
                    </FormGroup>
                    : ''
            }
            {
                errorCaption
                    ? <Typography variant="caption" color="red">{errorCaption}</Typography>
                    : ''
            }
            <Stack
                flexDirection="row"
                justifyContent="flex-start"
                {...actionsContainerProps?.container}
            >
                <Button
                    sx={{marginTop: 1, marginBottom: 1, flexGrow: 0}}
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth={buttonFullWidth}
                    disabled={isSubmitted || !isFormValid || isButtonDisabled}
                    {...actionsContainerProps?.button}
                >
                    {buttonText}
                </Button>
            </Stack>
        </form>
    );
};

AdmicityForm.defaultProps = {
    errorCaption: '',
    handleChange: () => {
    },
    buttonFullWidth: true
};

AdmicityForm.propTypes = {
    title: PropTypes.string,
    textFields: PropTypes.array,
    options: PropTypes.array,
    handleSubmit: PropTypes.func,
    handleChange: PropTypes.func,
    setIsChildFormValid: PropTypes.func,
    errorCaption: PropTypes.string,
    buttonText: PropTypes.string,
    buttonFullWidth: PropTypes.bool,
    isButtonDisabled: PropTypes.bool,
    actionsContainerProps: PropTypes.object,
};

export default React.forwardRef(AdmicityForm);